import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  alpha,
  Container,
  Fade,
  LinearProgress,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Work as WorkIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { format, isSameDay, parseISO } from 'date-fns';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EmployeeTaskPDF from './EmployeeTaskPDF';
import { marked } from 'marked';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const EmployeeTasks = () => {
  const theme = useTheme();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedDates, setSelectedDates] = useState([dayjs()]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  
  // Preview dialog states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeFormat, setTimeFormat] = useState('24hour');
  const [companyName, setCompanyName] = useState('');
  
  // Department dialog states
  const [departmentSelectionDialogOpen, setDepartmentSelectionDialogOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [tempSelectedDepartment, setTempSelectedDepartment] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchEmployees(),
          fetchDepartments(),
          fetchTasks(),
          fetchUserPreferences(),
          fetchCompanyName()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [companyId, selectedDates]);

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      // Get date range from selected dates
      let dates = [];
      
      if (selectedDates.length === 2) {
        // Generate all dates between start and end
        const startDate = selectedDates[0];
        const endDate = selectedDates[1];
        const daysBetween = endDate.diff(startDate, 'day');
        
        for (let i = 0; i <= daysBetween; i++) {
          dates.push(startDate.add(i, 'day').format('YYYY-MM-DD'));
        }
      } else {
        dates = selectedDates.map(date => date.format('YYYY-MM-DD'));
      }
      
      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          *,
          employee:employees(*),
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .in('shift_date', dates)
        .order('shift_date')
        .order('start_time');

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (preferences) {
        setTimeFormat(preferences.time_format || '12hour');
      } else {
        await createUserPreferences(user.id);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    }
  };

  const createUserPreferences = async (userId) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: userId,
          time_format: '12hour'
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat('12hour');
    } catch (err) {
      console.error('Error creating user preferences:', err);
    }
  };

  const fetchCompanyName = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompanyName(data?.name || '');
    } catch (err) {
      console.error('Error fetching company name:', err);
      setCompanyName('');
    }
  };

  // Filter employees based on search and department
  useEffect(() => {
    let filtered = employees;
    
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department_id === selectedDepartment);
    }
    
    setFilteredEmployees(filtered);
  }, [employees, searchTerm, selectedDepartment]);

  const getEmployeeTasks = (employeeId) => {
    return tasks.filter(task => task.employee_id === employeeId);
  };

  const calculateEmployeeStats = (employeeId) => {
    const empTasks = getEmployeeTasks(employeeId);
    
    return {
      totalTasks: empTasks.length
    };
  };


  const addDateRange = () => {
    const today = dayjs();
    const startOfWeek = today.startOf('week').add(1, 'day'); // Monday
    const endOfWeek = today.startOf('week').add(5, 'day'); // Friday
    setSelectedDates([startOfWeek, endOfWeek]);
  };

  const removeDate = (index) => {
    setSelectedDates(prev => prev.filter((_, i) => i !== index));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('');
    setSelectedDates([dayjs()]);
  };

  const openPreview = (employee) => {
    setSelectedEmployee(employee);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setSelectedEmployee(null);
  };

  // Department dialog functions
  const handleOpenDepartmentSelectionDialog = () => {
    setTempSelectedDepartment(selectedDepartment);
    setDepartmentSelectionDialogOpen(true);
    setDepartmentSearchTerm('');
  };

  const handleCloseDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setTempSelectedDepartment('');
  };

  const handleDepartmentToggle = (departmentId) => {
    setTempSelectedDepartment(tempSelectedDepartment === departmentId ? '' : departmentId);
  };

  const handleConfirmDepartmentSelection = () => {
    setSelectedDepartment(tempSelectedDepartment);
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setTempSelectedDepartment('');
  };

  const getFilteredDepartmentsForSelection = () => {
    if (!departmentSearchTerm) return departments || [];
    
    const searchLower = departmentSearchTerm.toLowerCase();
    return (departments || []).filter(department => 
      department.name.toLowerCase().includes(searchLower)
    );
  };

  const getDepartmentName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown';
  };

  const formatTime = (time) => {
    if (!time) return '';
    const parsed = parseISO(`2000-01-01T${time}`);
    return format(parsed, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a');
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(parseISO(date), 'MMMM d, yyyy');
  };

  const getDateRange = () => {
    if (selectedDates.length === 0) return '';
    if (selectedDates.length === 1) {
      return selectedDates[0].format('MMMM D, YYYY');
    }
    const sortedDates = [...selectedDates].sort((a, b) => a.diff(b));
    return `${sortedDates[0].format('MMMM D, YYYY')} - ${sortedDates[sortedDates.length - 1].format('MMMM D, YYYY')}`;
  };

  const handleDownloadPDF = (employee) => {
    const empTasks = getEmployeeTasks(employee.id);
    const stats = calculateEmployeeStats(employee.id);
    const dateRange = getDateRange();
    
    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    
    document.body.appendChild(iframe);
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Employee Task Sheet - ${employee.first_name} ${employee.last_name}</title>
          <style>
            @media print {
              body { margin: 0; padding: 15mm; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 11pt; line-height: 1.5; }
              .no-print { display: none !important; }
              .page-break { page-break-before: always; }
              .stats-grid { gap: 8px; margin-bottom: 15mm; }
              .shift-item { margin-bottom: 6px; padding: 10px; }
              .tasks-body { padding: 12px; }
              .header-grid { margin-bottom: 12mm; }
              .stats-container { margin-bottom: 12mm; }
              .shifts-container { margin-bottom: 12mm; }
              .tasks-container { margin-bottom: 8mm; }
            }
            body { 
              margin: 0; 
              padding: 32px; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              font-size: 14px; 
              line-height: 1.5; 
              color: #111827; 
              background: #ffffff;
            }

            /* Clean header like dialog */
            .header-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 40px;
              margin-bottom: 40px;
              padding-bottom: 24px;
              border-bottom: 1px solid #e5e7eb;
            }
            .header-item {
              text-align: left;
            }
            .header-label {
              font-size: 13px;
              font-weight: 400;
              color: #6b7280;
              margin-bottom: 4px;
            }
            .header-value {
              font-size: 18px;
              font-weight: 400;
              color: #111827;
              margin: 0;
            }

            /* Clean stats grid like dialog */
            .stats-container {
              margin-bottom: 40px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
            }
            .stat-box {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
            }
            .stat-title {
              font-size: 13px;
              font-weight: 400;
              color: #6b7280;
              margin-bottom: 8px;
            }
            .stat-number {
              font-size: 24px;
              font-weight: 500;
              margin: 0;
            }
            .stat-number.green { color: #10b981; }

            /* Clean tasks section like dialog */
            .tasks-container {
              margin-bottom: 40px;
            }
            .section-header {
              font-size: 18px;
              font-weight: 400;
              color: #111827;
              margin-bottom: 20px;
            }
            .task-item {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 12px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .task-left {
              flex: 1;
            }
            .task-date-text {
              font-size: 16px;
              font-weight: 400;
              color: #111827;
              margin: 0 0 4px 0;
            }
            .task-position {
              font-size: 14px;
              color: #6b7280;
              margin: 0;
            }
            .task-time-badge {
              background: #dbeafe;
              color: #1e40af;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 500;
            }

            /* Clean task documentation section like dialog */
            .task-docs-container {
              margin-bottom: 32px;
              page-break-before: always;
            }
            .tasks-header {
              background: transparent;
              border: none;
              padding: 0 0 16px 0;
              margin-bottom: 0;
            }
            .tasks-title {
              margin: 0;
              font-size: 18px;
              font-weight: 400;
              color: #111827;
            }
            .tasks-body {
              background: transparent;
              border: none;
              padding: 0;
            }

            /* Clean markdown styling */
            h1, h2, h3, h4, h5, h6 { 
              margin-top: 1.5em; 
              margin-bottom: 0.5em; 
              font-weight: 500; 
              line-height: 1.3;
            }
            h1 { font-size: 20px; color: #111827; }
            h2 { font-size: 18px; color: #111827; }
            h3 { font-size: 16px; color: #111827; }
            h4 { font-size: 15px; color: #111827; }
            p { margin-bottom: 1em; color: #374151; line-height: 1.6; }
            ul, ol { margin: 1em 0; padding-left: 1.5em; color: #374151; }
            li { margin-bottom: 0.25em; line-height: 1.5; }
            blockquote { 
              border-left: 4px solid #1976d2; 
              background-color: #f0f9ff; 
              padding: 12px 16px; 
              margin: 16px 0; 
              border-radius: 4px; 
            }
            code { 
              background-color: #f3f4f6; 
              color: #1f2937;
              padding: 2px 6px; 
              border-radius: 4px; 
              font-family: 'SF Mono', Monaco, Consolas, monospace; 
              font-size: 0.85em; 
            }
            pre { 
              background-color: #f9fafb; 
              border: 1px solid #e5e7eb; 
              border-radius: 6px; 
              padding: 16px; 
              margin: 16px 0; 
              overflow: auto; 
              font-family: 'SF Mono', Monaco, Consolas, monospace; 
              font-size: 0.875em; 
              line-height: 1.5; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              border: 1px solid #e5e7eb; 
              margin: 16px 0; 
              border-radius: 6px;
              overflow: hidden;
            }
            th, td { 
              padding: 8px 12px; 
              border-bottom: 1px solid #e5e7eb; 
              text-align: left; 
              font-size: 13px;
            }
            th { 
              background-color: #f9fafb; 
              font-weight: 500; 
              color: #374151;
            }
            hr { 
              border: none; 
              border-top: 1px solid #e5e7eb; 
              margin: 20px 0; 
            }

            @page { 
              margin: 8mm; 
              size: A4;
              @top-left { content: ""; }
              @top-center { content: ""; }
              @top-right { content: ""; }
              @bottom-left { content: ""; }
              @bottom-center { content: ""; }
              @bottom-right { content: ""; }
            }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>
          ${companyName ? `
          <div style="text-align: right; margin-bottom: 24px;">
            <h2 style="margin: 0; font-size: 24px; font-weight: 500; color: #111827;">${companyName}</h2>
          </div>
          ` : ''}

          <div class="header-grid">
            <div class="header-item">
              <div class="header-label">Employee Name</div>
              <div class="header-value">${employee.first_name} ${employee.last_name}</div>
            </div>
            <div class="header-item">
              <div class="header-label">Department</div>
              <div class="header-value">${employee.department?.name || 'No Department'}</div>
            </div>
            <div class="header-item">
              <div class="header-label">Period</div>
              <div class="header-value">${dateRange}</div>
            </div>
          </div>

          <div class="stats-container">
            <div class="stats-grid">
              <div class="stat-box">
                <div class="stat-title">Total Tasks</div>
                <div class="stat-number green">${stats.totalTasks}</div>
              </div>
            </div>
          </div>

          <div class="tasks-container">
            <div class="section-header">Scheduled Tasks</div>
            ${empTasks.length > 0 ? empTasks.map(task => `
              <div class="task-item">
                <div class="task-left">
                  <div class="task-date-text">${formatDate(task.shift_date)}</div>
                  ${task.job_position ? `<div class="task-position">Position: ${task.job_position}</div>` : ''}
                </div>
                <div class="task-time-badge">${formatTime(task.start_time)} - ${formatTime(task.end_time)}</div>
              </div>
            `).join('') : '<p style="color: #6b7280; text-align: center; padding: 32px 0;">No tasks scheduled for selected dates</p>'}
          </div>

          ${empTasks.some(task => task.role_documentation) ? `
            <div class="task-docs-container">
              <div class="tasks-header">
                <div class="tasks-title">Primary Responsibilities & Tasks</div>
              </div>
              <div class="tasks-body">
                ${empTasks.filter(task => task.role_documentation).map((task, index) => `
                  <div ${index > 0 ? 'class="page-break"' : ''} style="margin-bottom: ${index < empTasks.filter(s => s.role_documentation).length - 1 ? '24px' : '0'};">
                    ${empTasks.filter(s => s.role_documentation).length > 1 ? `
                      <h3 style="color: #1976d2; margin-bottom: 12px; font-size: 16px; font-weight: 500;">${formatDate(task.shift_date)} - ${task.job_position || 'General tasks'}</h3>
                    ` : ''}
                    <div>
                      ${marked.parse(task.role_documentation)}
                    </div>
                    ${index < empTasks.filter(s => s.role_documentation).length - 1 ? '<div style="margin: 20px 0; border-top: 1px solid #e5e7eb;"></div>' : ''}
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
        </body>
      </html>
    `;

    // Write content to iframe and print
    iframe.contentDocument.open();
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };

  // Permission check
  if (!hasPermission('schedule.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Schedule Management Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Employee Tasks. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#673AB7', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#673AB7', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#673AB7', 0.1),
                mb: 2
              }}>
                <AssignmentIcon sx={{ fontSize: 48, color: '#673AB7' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#673AB7',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Employee Tasks
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Preparing employee shift data and task documentation...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#673AB7',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container
        maxWidth="xl"
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {/* Page Header */}
        <Card sx={{
          borderRadius: 3,
          mt: 4,
          mb: 4,
          border: 'none',
          background: 'white',
          boxShadow: 'none',
          borderTop: '3px solid #673AB7',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha('#673AB7', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#673AB7', 0.08),
              }}>
                <AssignmentIcon sx={{ fontSize: 32, color: '#673AB7' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#673AB7', 
                  mb: 0.5,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Employee Tasks & Documentation
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  Generate detailed task sheets for employees with shift schedules and role documentation
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon sx={{ fontSize: 20 }} />
              Search & Filter
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search Employees"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, email, or position..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenDepartmentSelectionDialog}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1,
                    px: 2,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    border: `1px solid rgba(0,0,0,0.1)`,
                    color: selectedDepartment ? 'text.primary' : 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      border: `1px solid rgba(0,0,0,0.2)`,
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  {selectedDepartment ? getDepartmentName(selectedDepartment) : 'All Departments'}
                </Button>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  sx={{ height: 56, borderRadius: 2, fontWeight: 500 }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
            
            {/* Date Selection */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon sx={{ fontSize: 18 }} />
                Select Dates for Task Sheet
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {selectedDates.map((date, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DatePicker
                        value={date}
                        onChange={(newDate) => {
                          if (!newDate) return;
                          
                          const newDates = [...selectedDates];
                          newDates[index] = newDate;
                          
                          // Sort dates to maintain chronological order
                          newDates.sort((a, b) => a.diff(b));
                          
                          setSelectedDates(newDates);
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 150 }
                          }
                        }}
                      />
                      {selectedDates.length > 1 && (
                        <Button
                          size="small"
                          onClick={() => removeDate(index)}
                          sx={{ minWidth: 32, p: 1 }}
                        >
                          ×
                        </Button>
                      )}
                    </Box>
                  ))}
                </LocalizationProvider>
              </Box>
              
              <Button
                variant="outlined"
                size="small"
                onClick={addDateRange}
                startIcon={<DateRangeIcon />}
                sx={{ borderRadius: 2, fontWeight: 500 }}
                >
                  Add Date Range
                </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Employee Results */}
        <Grid container spacing={3}>
          {filteredEmployees.map((employee) => {
            const empTasks = getEmployeeTasks(employee.id);
            const stats = calculateEmployeeStats(employee.id);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={employee.id}>
                <Card sx={{ 
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.grey[400]}`,
                  background: 'white',
                  backdropFilter: 'blur(20px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Employee Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        width: 48, 
                        height: 48, 
                        bgcolor: employee.department?.color || '#1976d2'
                      }}>
                        {employee.first_name?.[0]}{employee.last_name?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {employee.first_name} {employee.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {employee.department?.name || 'No Department'}
                        </Typography>
                        {employee.job_title && (
                          <Typography variant="caption" color="text.secondary">
                            {employee.job_title}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Task Stats */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScheduleIcon sx={{ fontSize: 16 }} />
                          Tasks:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {stats.totalTasks}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Tasks List */}
                    {empTasks.length > 0 ? (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                          Scheduled Tasks:
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                          {empTasks.slice(0, 3).map((task, index) => (
                            <ListItem key={task.id} sx={{ px: 0, py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                     {dayjs(task.shift_date).format('MMM D')} - {formatTime(task.start_time)} to {formatTime(task.end_time)}
                                  </Typography>
                                }
                                secondary={task.job_position || 'No position specified'}
                                secondaryTypographyProps={{ 
                                  variant: 'caption', 
                                  color: 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              />
                              {task.role_documentation && (
                                <Chip
                                  size="small"
                                  label="Has Documentation"
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '0.6rem',
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: 'success.main'
                                  }}
                                />
                              )}
                            </ListItem>
                          ))}
                          {empTasks.length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 0, fontSize: '0.7rem' }}>
                              +{empTasks.length - 3} more tasks
                            </Typography>
                          )}
                        </List>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No tasks scheduled for selected dates
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Generate PDF Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PdfIcon />}
                      onClick={() => openPreview(employee)}
                      disabled={empTasks.length === 0}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        py: 1.5,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                        },
                        '&:disabled': {
                          opacity: 0.6
                        }
                      }}
                    >
                      Generate Task Sheet Preview
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          
          {filteredEmployees.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                textAlign: 'center',
                py: 6
              }}>
                <CardContent>
                  <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    No Employees Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search criteria or filters
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Task Preview Dialog */}
        {selectedEmployee && (
          <Dialog
            open={previewOpen}
            onClose={closePreview}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`,
                maxHeight: '90vh'
              }
            }}
          >
            <DialogTitle sx={{ 
              fontWeight: 500, 
              color: 'primary.main',
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AssignmentIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                Employee Task Sheet Preview
              </Box>
              
              <IconButton onClick={closePreview} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
              {(() => {
                const empTasks = getEmployeeTasks(selectedEmployee.id);
                const stats = calculateEmployeeStats(selectedEmployee.id);
                
                return (
                  <Grid container spacing={3}>
                    {/* Employee Header Info */}
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 3, 
                        mt: 4,
                        borderRadius: 3, 
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        mb: 2
                      }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  Employee Name
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                  {selectedEmployee.first_name} {selectedEmployee.last_name}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  Department
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                  {selectedEmployee.department?.name || 'No Department'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <DateRangeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  Period
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                  {getDateRange()}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Stats Summary */}
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            textAlign: 'center'
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              Total Tasks
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: 'success.main' }}>
                              {stats.totalTasks}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Scheduled Tasks */}
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        backgroundColor: alpha(theme.palette.grey[100], 0.3),
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                          Scheduled Tasks
                        </Typography>
                        
                        {empTasks.length > 0 ? (
                          <Grid container spacing={2}>
                            {empTasks.map((task, index) => (
                              <Grid item xs={12} md={6} key={task.id}>
                                <Box sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  backgroundColor: 'white',
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.05)}`
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                      {formatDate(task.shift_date)}
                                    </Typography>
                                    <Chip 
                                      size="small" 
                                      label={`${formatTime(task.start_time)} - ${formatTime(task.end_time)}`}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                  
                                  {task.job_position && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Position: {task.job_position}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No tasks scheduled for selected dates
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Role Documentation */}
                    {empTasks.some(task => task.role_documentation) && (
                      <Grid item xs={12}>
                        <Box sx={{ 
                          borderRadius: 3, 
                          overflow: 'hidden',
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          <Box sx={{ 
                            p: 3, 
                            pb: 2,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 3,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AssignmentIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: 'primary.main',
                                fontSize: '1.2rem',
                                letterSpacing: '0.5px'
                              }}>
                                Primary Responsibilities & Tasks
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ 
                            p: 3,
                            backgroundColor: 'white',
                            maxHeight: '50vh',
                            overflow: 'auto'
                          }}>
                            {empTasks.filter(task => task.role_documentation).map((task, index) => (
                              <Box key={task.id} sx={{ mb: index < empTasks.filter(s => s.role_documentation).length - 1 ? 4 : 0 }}>
                                {empTasks.filter(s => s.role_documentation).length > 1 && (
                                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                                    {formatDate(task.shift_date)} - {task.job_position || 'General Tasks'}
                                  </Typography>
                                )}
                                
                                <ReactMarkdown 
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    h1: ({ children }) => (
                                      <Typography variant="h4" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                                        {children}
                                      </Typography>
                                    ),
                                    h2: ({ children }) => (
                                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, mt: 3, color: 'primary.main' }}>
                                        {children}
                                      </Typography>
                                    ),
                                    h3: ({ children }) => (
                                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, mt: 2.5, color: 'text.primary' }}>
                                        {children}
                                      </Typography>
                                    ),
                                    p: ({ children }) => (
                                      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.primary' }}>
                                        {children}
                                      </Typography>
                                    ),
                                    ul: ({ children }) => (
                                      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                        {children}
                                      </Box>
                                    ),
                                    ol: ({ children }) => (
                                      <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                                        {children}
                                      </Box>
                                    ),
                                    li: ({ children }) => (
                                      <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                                        {children}
                                      </Typography>
                                    ),
                                    blockquote: ({ children }) => (
                                      <Box sx={{
                                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                        p: 2,
                                        my: 2,
                                        borderRadius: 1
                                      }}>
                                        {children}
                                      </Box>
                                    ),
                                    code: ({ inline, className, children, ...props }) => {
                                      if (inline) {
                                        return (
                                          <code
                                            style={{
                                              backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                              fontSize: '0.85em',
                                              color: theme.palette.text.primary
                                            }}
                                            {...props}
                                          >
                                            {children}
                                          </code>
                                        );
                                      }
                                      return (
                                        <pre
                                          style={{
                                            backgroundColor: alpha(theme.palette.grey[900], 0.05),
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                            borderRadius: '8px',
                                            padding: '16px',
                                            margin: '16px 0',
                                            overflow: 'auto',
                                            fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                            fontSize: '0.9em',
                                            lineHeight: '1.6',
                                            color: theme.palette.text.primary,
                                            whiteSpace: 'pre',
                                            tabSize: 4
                                          }}
                                        >
                                          <code {...props}>{children}</code>
                                        </pre>
                                      );
                                    },
                                    table: ({ children }) => (
                                      <Box sx={{ overflowX: 'auto', my: 2 }}>
                                        <table style={{
                                          width: '100%',
                                          borderCollapse: 'collapse',
                                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                        }}>
                                          {children}
                                        </table>
                                      </Box>
                                    ),
                                    th: ({ children }) => (
                                      <th style={{
                                        padding: '12px',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                        fontWeight: 500,
                                        textAlign: 'left'
                                      }}>
                                        {children}
                                      </th>
                                    ),
                                    td: ({ children }) => (
                                      <td style={{
                                        padding: '12px',
                                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                      }}>
                                        {children}
                                      </td>
                                    ),
                                    hr: () => (
                                      <Divider sx={{ my: 3 }} />
                                    )
                                  }}
                                >
                                  {task.role_documentation}
                                </ReactMarkdown>
                                
                                {index < empTasks.filter(s => s.role_documentation).length - 1 && (
                                  <Divider sx={{ my: 3 }} />
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                );
              })()}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Box />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={closePreview}
                  variant="outlined"
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadPDF(selectedEmployee)}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}
                >
                  Download PDF
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        )}

        {/* Department Selection Dialog */}
        <Dialog
          open={departmentSelectionDialogOpen}
          onClose={handleCloseDepartmentSelectionDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 4,
              height: '40vh',
              width: '600px',
              maxWidth: '600px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Department
            </Typography>
            <IconButton onClick={handleCloseDepartmentSelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search departments..."
                value={departmentSearchTerm}
                onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow 
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          All Departments
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Show employees from all departments
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDepartmentToggle('')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          bgcolor: tempSelectedDepartment === '' ? 'black' : 'transparent',
                          color: tempSelectedDepartment === '' ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: tempSelectedDepartment === '' ? 'black' : 'transparent',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        {tempSelectedDepartment === '' ? 'Selected' : 'Select'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {getFilteredDepartmentsForSelection().map((department) => (
                    <TableRow 
                      key={department.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {department.name}
                          </Typography>
                          {department.description && (
                            <Typography variant="caption" color="text.secondary">
                              {department.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDepartmentToggle(department.id)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            bgcolor: tempSelectedDepartment === department.id ? 'black' : 'transparent',
                            color: tempSelectedDepartment === department.id ? 'white' : 'inherit',
                            '&:hover': {
                              bgcolor: tempSelectedDepartment === department.id ? 'black' : 'transparent',
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          {tempSelectedDepartment === department.id ? 'Selected' : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredDepartmentsForSelection().length === 0 && departmentSearchTerm && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No departments found matching your search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Typography variant="body2" color="text.secondary">
              {tempSelectedDepartment ? getDepartmentName(tempSelectedDepartment) || 'All Departments' : 'All Departments'} selected
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseDepartmentSelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDepartmentSelection}
                variant="contained"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: 'black',
                  color: 'white',
                  border: '1px solid black',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.3)}`,
                  '&:hover': {
                    background: 'black',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 16px ${alpha('#000', 0.4)}`
                  }
                }}
              >
                Apply Filter
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default EmployeeTasks;